import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { maBeratungContent } from '../data/pageContent';

const MABeratungPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <Helmet>
        <title>{maBeratungContent.title}</title>
        <meta name="description" content={maBeratungContent.description} />
        <link rel="canonical" href="https://www.unternehmenbewerten.ch/ma-beratung" />
      </Helmet>
      
      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 lg:py-24 bg-gradient-to-b from-[#f8f9fa] to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl">
              <nav className="text-sm text-[#5a5d5b] mb-4">
                <Link to="/" className="hover:text-[#FF0000]">Home</Link>
                <span className="mx-2">/</span>
                <span>M&amp;A Beratung</span>
              </nav>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1a1c1b] mb-6">
                {maBeratungContent.h1}
              </h1>
              <p className="text-lg text-[#5a5d5b] mb-4">
                {maBeratungContent.intro}
              </p>
              <p className="text-[#5a5d5b] mb-8">
                {maBeratungContent.introExtended}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                  M&amp;A Erstgespräch anfragen
                  <ArrowRight size={18} />
                </Link>
                <Link to="/bewertung" className="inline-flex items-center justify-center px-6 py-3 border-2 border-[#1a1c1b] text-[#1a1c1b] font-semibold rounded-lg hover:bg-[#1a1c1b] hover:text-white transition-colors">
                  Erst bewerten
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Unsere M&A-Leistungen */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Unsere M&amp;A-Leistungen
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {maBeratungContent.servicesIntro}
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              {maBeratungContent.services.map((service) => (
                <div key={service.title} className="bg-[#f8f9fa] rounded-xl p-6 border border-gray-100">
                  {service.link ? (
                    <Link to={service.link} className="text-[#FF0000] font-semibold mb-2 block hover:underline">{service.title}</Link>
                  ) : (
                    <h3 className="text-[#1a1c1b] font-semibold mb-2">{service.title}</h3>
                  )}
                  <p className="text-[#5a5d5b] text-sm">{service.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ablauf einer M&A-Transaktion */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Ablauf einer M&amp;A-Transaktion mit uns
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {maBeratungContent.processIntro}
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
              {maBeratungContent.processSteps.map((step) => (
                <div key={step.step} className="bg-white rounded-xl p-5 border border-gray-100">
                  <div className="w-10 h-10 rounded-full bg-[#FF0000] text-white flex items-center justify-center font-bold mb-3">
                    {step.step}
                  </div>
                  <h3 className="text-[#1a1c1b] font-semibold mb-2 text-sm">{step.title}</h3>
                  <p className="text-[#5a5d5b] text-xs">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Referenzen Teaser */}
        <section className="py-12 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100 flex flex-col md:flex-row items-center gap-6">
              <div className="flex-1">
                <h3 className="text-xl font-bold text-[#1a1c1b] mb-2">Praxisbeispiele aus unserer Beratung</h3>
                <p className="text-[#5a5d5b]">Sehen Sie, wie eine Nachfolgeregelung bei uns in der Praxis ablaufen kann – von MBO über MBI bis zum Verkauf an Investoren.</p>
              </div>
              <Link to="/referenzen" className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors whitespace-nowrap">
                Referenzen ansehen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Warum professionelle M&A-Beratung? */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Warum professionelle M&amp;A-Beratung?
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8 max-w-3xl mx-auto">
              {maBeratungContent.benefitsIntro}
            </p>
            <div className="bg-[#f8f9fa] rounded-2xl p-8 border border-gray-100 max-w-3xl mx-auto">
              <ul className="space-y-4">
                {maBeratungContent.benefits.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#FF0000] flex-shrink-0 mt-0.5" />
                    <span className="text-[#1a1c1b]">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Was kostet eine M&A-Beratung? */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-4 text-center">
              Was kostet eine M&amp;A-Beratung?
            </h2>
            <p className="text-[#5a5d5b] text-center mb-8">
              {maBeratungContent.pricing.intro}
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {maBeratungContent.pricing.models.map((model) => (
                <div key={model.title} className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#FF0000] font-semibold mb-2">{model.title}</h3>
                  <p className="text-[#5a5d5b] text-sm">{model.desc}</p>
                </div>
              ))}
            </div>
            <p className="text-[#5a5d5b] leading-relaxed mb-4">
              {maBeratungContent.pricing.note}
            </p>
            {/* Absatz 2a: Link zum Kostenartikel - muss identisch sein mit SSG */}
            {maBeratungContent.pricing.detailLink && (
              <p className="text-[#5a5d5b] leading-relaxed">
                {maBeratungContent.pricing.detailLink.text}{' '}
                <Link 
                  to={maBeratungContent.pricing.detailLink.href} 
                  className="text-[#FF0000] hover:underline"
                >
                  {maBeratungContent.pricing.detailLink.linkText}
                </Link>
              </p>
            )}
          </div>
        </section>

        {/* Ihre Vorteile */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-6 text-center">
              Ihre Vorteile mit unternehmenbewerten.ch
            </h2>
            <p className="text-[#5a5d5b] leading-relaxed mb-8">
              {maBeratungContent.trustSection}
            </p>
            <p className="text-[#5a5d5b]">
              Über CHF 400 Mio. begleitetes Transaktionsvolumen. Ob Sie{' '}
              <Link to="/unternehmen-verkaufen" className="text-[#FF0000] hover:underline">Ihr Unternehmen verkaufen</Link> oder{' '}
              <Link to="/unternehmen-kaufen" className="text-[#FF0000] hover:underline">ein Unternehmen kaufen</Link> möchten – wir sind Ihr Partner für erfolgreiche Transaktionen.
            </p>
            <div className="text-center mt-8">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
                M&amp;A Erstgespräch anfragen
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>

        {/* Häufige Fragen */}
        <section className="py-16 bg-[#f8f9fa]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#1a1c1b] mb-8 text-center">
              Häufige Fragen zur M&amp;A-Beratung
            </h2>
            <div className="space-y-6">
              {maBeratungContent.faq.map((item) => (
                <div key={item.question} className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="text-[#1a1c1b] font-semibold mb-3">{item.question}</h3>
                  <p className="text-[#5a5d5b] leading-relaxed">
                    {item.answer}
                    {/* Render answer link if present - must match SSG */}
                    {item.answerLink && (
                      <>
                        {' '}
                        <Link 
                          to={item.answerLink.href} 
                          className="text-[#FF0000] hover:underline"
                        >
                          {item.answerLink.text}
                        </Link>
                      </>
                    )}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#1a1c1b]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Bereit für Ihre Transaktion?
            </h2>
            <p className="text-gray-300 mb-8">
              Kontaktieren Sie uns für ein unverbindliches Erstgespräch.
            </p>
            <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#FF0000] text-white font-semibold rounded-lg hover:bg-[#e60000] transition-colors">
              Erstgespräch anfragen
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      </main>
      
      <Footer />
      {/* Schemas werden ausschliesslich via SSG geliefert - keine Duplikate durch React */}
    </div>
  );
};

export default MABeratungPage;
